/* Studio brand logo / wordmark.

   Resolution order:
   1. `settings.logo_url` (tenant uploaded a logo)  → render the image
   2. `settings.studio_name` (tenant has a name)    → render typographic wordmark
   3. Fallback to the bundled Allure Image PNG     → the founder studio

   This keeps every tenant's branding theirs — no Allure logo accidentally
   stamped on Maya Chen Photography's site — while making sure new studios
   that haven't uploaded a logo yet still get a polished, editorial wordmark
   (Cormorant Garamond, generous letter-spacing) instead of an empty box. */

import { useSiteSettings } from "@/hooks/useSiteSettings";

const ALLURE_STUDIO_NAME = "Allure Image";

export default function Logo({ className = "h-8", testid = "brand-logo" }) {
  const { settings } = useSiteSettings();
  const logoUrl = settings?.logo_url?.trim();
  const studioName = (settings?.studio_name || "").trim();

  // 1) Uploaded logo — image renders at the requested height, preserving aspect ratio.
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={studioName || ALLURE_STUDIO_NAME}
        loading="eager"
        fetchPriority="high"
        decoding="async"
        data-testid={testid}
        className={`${className} w-auto object-contain object-left select-none max-w-[200px] sm:max-w-[260px]`}
        draggable={false}
      />
    );
  }

  // 2) Tenant with a studio_name but no uploaded logo — typographic wordmark.
  // Skipped for the Allure studio so its existing graphic stays the default.
  if (studioName && studioName !== ALLURE_STUDIO_NAME) {
    return (
      <span
        data-testid={testid}
        className={`${className} inline-flex items-center select-none font-light tracking-[0.18em] uppercase text-foreground text-base sm:text-lg whitespace-nowrap max-w-[220px] sm:max-w-none truncate`}
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: "0.18em" }}
      >
        {studioName}
      </span>
    );
  }

  // 3) Allure default — original picture element with WebP-first source.
  return (
    <picture>
      <source srcSet="/logo.webp" type="image/webp" />
      <img
        src="/logo.png"
        alt="Allure Image"
        width={520}
        height={199}
        loading="eager"
        fetchPriority="high"
        decoding="async"
        data-testid={testid}
        className={`${className} w-auto object-contain object-left select-none max-w-[180px] sm:max-w-none`}
        draggable={false}
      />
    </picture>
  );
}
